import { initializeApp } from 'firebase/app';
import { deleteToken, getMessaging, getToken, isSupported } from 'firebase/messaging';
import { useMemo } from 'react';

const firebaseConfig = {
  apiKey: 'AIzaSyClw1Pix5ZkQAEX6GyWsIfLm2BFlqQ2Sw4',
  authDomain: 'teamplay-gg.firebaseapp.com',
  projectId: 'teamplay-gg',
  storageBucket: 'teamplay-gg.appspot.com',
  messagingSenderId: '834357849573',
  appId: '1:834357849573:web:1e965236043aee7169530c',
  measurementId: 'G-4V2S5GXZ46',
};

const VAPID_KEY =
  'BGdA0ymoAUUAH5HAN6ezyoZXhTOiLEK_XEHgKR9iAOikrsbpWvbcFwq8vS7kSSTCWpUi4y-kt_Sr8iGt9chIDQs';

type FirebaseClient = {
  getPushToken: () => Promise<string | null>;
  deletePushToken: () => Promise<void>;
};

let firebaseClient: FirebaseClient | null = null;

function createFirebaseClient() {
  if (typeof window === 'undefined') {
    return null;
  }

  const app = initializeApp(firebaseConfig);

  let messagingPromise: Promise<ReturnType<typeof getMessaging> | null> | null = null;

  const getMessagingIfSupported = () => {
    if (!messagingPromise) {
      messagingPromise = isSupported()
        .then((supported) => {
          if (!supported) {
            console.warn('Firebase messaging is not supported in this browser');
            return null;
          }

          return getMessaging(app);
        })
        .catch((error) => {
          console.warn('Failed to check Firebase messaging support', error);
          return null;
        });
    }

    return messagingPromise;
  };

  const getPushToken = async () => {
    const messaging = await getMessagingIfSupported();

    if (!messaging) {
      return null;
    }

    // Firebase SDK assumes navigator.serviceWorker.getRegistration() returns a
    // valid registration. After a user toggles the site-level "Notifications"
    // permission via the omnibox, the SW can be in a state where the lookup
    // resolves to undefined and Firebase blows up with "Cannot read
    // properties of undefined (reading 'pushManager')". Resolve the
    // registration ourselves and pass it explicitly so we control the failure.
    let registration: ServiceWorkerRegistration | undefined;
    try {
      if ('serviceWorker' in navigator) {
        registration =
          (await navigator.serviceWorker.getRegistration('/firebase-cloud-messaging-push-scope')) ??
          (await navigator.serviceWorker.getRegistration()) ??
          (await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
            scope: '/firebase-cloud-messaging-push-scope',
          }));
      }
    } catch (error) {
      console.warn('[firebase] Could not resolve service worker registration', error);
      return null;
    }

    if (!registration) {
      console.warn('[firebase] No service worker registration available');
      return null;
    }

    try {
      return await getToken(messaging, {
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration: registration,
      });
    } catch (error) {
      console.warn('[firebase] getToken failed', error);
      return null;
    }
  };

  const deletePushToken = async () => {
    const messaging = await getMessagingIfSupported();

    if (!messaging) {
      return;
    }

    await deleteToken(messaging);
  };

  return { getPushToken, deletePushToken };
}

export const initializeFirebaseClient = () => {
  try {
    const _firebaseClient = firebaseClient ?? createFirebaseClient();

    if (typeof window === 'undefined') {
      return null;
    }

    if (!firebaseClient) {
      firebaseClient = _firebaseClient;
    }

    return _firebaseClient;
  } catch (error) {
    console.error('Failed to initialize Firebase client', error);
    return null;
  }
};

export const useFirebase = () => {
  const client = useMemo(() => initializeFirebaseClient(), []);

  return client;
};
